import React from 'react';
import BrandComparison from './BrandComparison';
import CaseStudyBackLink from './CaseStudyBackLink';

// Images
//***************************/
import heroDesktopImage from '../img/case-studies/Flex Academy/flexAcademy_hero_desktop_example.webp';
import playbookRoadmapImage from '../img/case-studies/Flex Academy/playbook_roadmap_example.png';
import proofBandImage from '../img/case-studies/Flex Academy/flex_proofband_example.png';
import comparisonImage from '../img/case-studies/Flex Academy/comparison_example.png';
import registrationFlowsImage from '../img/case-studies/Flex Academy/flexAcademy_registrationFlows.png';
import mobileScreensImage from '../img/case-studies/Flex Academy/flexAcademy_mobileScreens_iPhone.png';

const images = {
  hero: { src: heroDesktopImage, alt: 'The Flex Academy hero on desktop, leading with the Launch path', caption: 'The hero leads with the Launch path and keeps Scale in view' },
  roadmap: { src: playbookRoadmapImage, alt: 'The four-step playbook roadmap, from picking a market to the first guest checking in', caption: 'A four-step route with a clear finish line' },
  proofBand: { src: proofBandImage, alt: 'The proof band showing The Flex results: rent paid, corporate housing partners, tenants placed and a 2027 properties goal', caption: "The Flex's published results, shown as proof the playbook works", plain: true },
  comparison: { src: comparisonImage, alt: 'The program comparison placing the Launch and Scale paths side by side', caption: 'Both paths side by side at the moment of choosing' },
  registrationFlows: { src: registrationFlowsImage, alt: 'Prototype connections for the webinar registration flow and the strategy call registration flow', caption: 'Both registration flows as connected in the Figma prototype' },
  mobile: { src: mobileScreensImage, alt: 'The mobile hero and the mobile program comparison on iPhone', caption: 'The same two paths at 390 pixels wide', plain: true },
};

// Links
//***************************/
const prototypeUrl = 'https://www.figma.com/proto/5Cnsa9uRCOHE8p1Ebf2ikb/The-Flex-Academy?node-id=1-2&viewport=4368%2C302%2C0.7&t=0UWjXgRp0G0ldNx0-0&scaling=min-zoom&content-scaling=fixed&starting-point-node-id=1%3A2';
const figmaFileUrl = 'https://www.figma.com/design/5Cnsa9uRCOHE8p1Ebf2ikb/The-Flex-Academy?node-id=1-6&t=kHl6fx4jcQPji4Jm-1';

function CaseStudyImage({ image, className = '' }) {
  return (
    <div className={`case-study-image ${className}`}>
      <img className={image.plain ? 'img-fluid' : 'img-fluid rounded shadow'} src={image.src} alt={image.alt} />
      <span className="image-caption">{image.caption}</span>
    </div>
  );
}

function FlexAcademy() {
  const pageSections = [
    { section: 'Hero', question: 'What is this, and is it for me?' },
    { section: 'Proof from The Flex', question: 'Can I trust these people?' },
    { section: 'Playbook roadmap', question: 'How does it actually work?' },
    { section: 'Founders', question: 'Who is behind it?' },
    { section: 'Program comparison', question: 'Which path is mine?' },
    { section: 'Partner quote and FAQ', question: "What's still holding me back?" },
    { section: 'Webinar sign-up and checklist', question: 'How do I start?' },
  ];

  const nextSteps = [
    { title: 'Webinar scheduling', description: 'Compare auto-assigned sessions with letting visitors pick their own time, using registration-to-attendance rate as the measure.' },
    { title: 'Local currency', description: "Show budget bands in each visitor's own currency." },
    { title: 'Mobile flows', description: 'Extend the mobile designs to both conversion flows.' },
    { title: 'Design system', description: 'Formalize the components and tokens, and expand button and form states.' },
    { title: 'Motion', description: 'Explore a motion concept for the hero and the playbook roadmap.' },
  ];

  const takeaways = [
    {
      title: 'Credibility comes from restraint',
      body: 'Designing for an audience that arrives skeptical shaped every section of this page. Credibility came from restraint: real numbers with clear labels, a roadmap with a concrete finish line, and commitments small enough to feel safe. Building the brand and the page together let every visual choice support that same goal, from a palette rooted in The Flex to an accent color saved for the actions that matter most.',
    },
    {
      title: "Finding a product's place in the family",
      body: 'Designing within a family of brands starts with finding where the new product sits among its siblings and what role it plays alongside them. From there, the work becomes identifying which design elements can carry through, such as a color, a typeface or a tone of voice, so the connection reads at a glance. Those shared threads make a new brand feel familiar from day one and still leave room for a voice of its own.',
    },
  ];

  return (
    <section id="caseStudy_flexAcademy" className="bg-light">
      <div className="bannerArea">
        <div className="bannerContent">
          <p className="wordmark">
            Flex <span>Academy</span>
          </p>
        </div>
      </div>

      <div className="container">
        <div className="row py-5 px-4">
          <div className="col-12 col-md-8">
            <h2>About</h2>
            <p>
              Flex Academy is a new education brand from the team behind The Flex, a short-term rental operator, and Base360, the property management software they built to run it. Academy helps people start and grow their own short-term rental business using the same playbook The Flex runs on. For a design assessment, I designed a landing page that introduces it, from the brand direction and page structure through both conversion flows and key mobile views.
            </p>
            <p className="disclaimer">
              Speculative concept created for a design assessment in October 2026. Independent work, unaffiliated with The Flex or Base360. The mockups use UK English for The Flex's London-based audience.
            </p>
            <h2>The Challenge</h2>
            <p>
              The page had to serve two very different visitors. The first is thinking about leaving a 9-to-5 job to start a short-term rental business, and their first step is a free webinar, which I called the Launch path. The second already runs a few units and wants to grow, and their next step is a strategy call on the Scale path. Launch is the larger market, so it leads the page, with Scale always one click away.
            </p>
            <p>
              The harder problem was trust. Short-term rental education is crowded with guru marketing built on rented supercars, poolside lifestyle shots and promises of unlimited passive income. This audience has seen all of it and arrives skeptical, so the page needed to feel grounded, specific and credible from the first screen, and earn that trust before asking for anything.
            </p>
          </div>
          <div className="col-12 col-md-4">
            <section id="projectComponents" className="pt-5 pt-md-0 px-0 px-md-4">
              <div>
                <h3>Project Components:</h3>
                <ul>
                  <li>Desktop Landing Page</li>
                  <li>Webinar Registration Flow</li>
                  <li>Strategy Call Flow</li>
                  <li>Mobile Experience</li>
                </ul>
              </div>
              <h3>My Role</h3>
              <ul>
                <li>Brand Direction</li>
                <li>Information Architecture</li>
                <li>UI Design</li>
                <li>Conversion Flows</li>
                <li>Design Rationale</li>
              </ul>
              <h3>Project Links</h3>
              <ul>
                <li><a href={prototypeUrl} target="_blank" rel="noreferrer">Figma Prototype</a></li>
                <li><a href={figmaFileUrl} target="_blank" rel="noreferrer">Figma Design File</a></li>
              </ul>
            </section>
          </div>
        </div>
      </div>

      <div className="container">
        <div className="row pb-5 px-4">
          <div className="col-12">
            <CaseStudyImage image={images.hero} />
          </div>
        </div>
      </div>

      <div className="container">
        <div className="row py-5 px-4 bg-white--rounded">
          <div className="col-12">
            <h2>The Approach</h2>
            <h3>A Sibling Brand</h3>
            <p>
              Flex Academy joins a family of two existing brands. Base360 is the underlying platform that runs the business, and The Flex, with furnished stays across seven cities since 2019, is the proof that the platform and its system work. Academy takes the lessons learned running The Flex and helps others achieve similar success, so I designed it as a natural sibling to both brands with a voice of its own.
            </p>
            <p>
              The palette starts with The Flex's warmth. Its green deepens into an ink tone, and an off-white paper color carries over the feel of its section backgrounds. Ochre is Academy's own accent, reserved for primary actions on both paths and always paired with ink text for contrast. A mist tone borrowed from The Flex marks Scale surfaces, and Base360's blue appears only where the product itself shows up. For type, Roboto Serif headings bridge The Flex's Georgia headlines and Base360's Roboto, with Roboto carrying the body text, which gives the page the feel of an operator's field guide.
            </p>
            <BrandComparison />
          </div>
        </div>
      </div>

      <div className="container">
        <div className="row py-5 px-4">
          <div className="col-12 col-md-7">
            <h3>Building Trust Step by Step</h3>
            <p>
              The page is sequenced to address the questions a skeptical visitor is likely to have as they come up, starting with what this is and who it's for and ending with how to get started. Each section builds on the trust earned by the one before it:
            </p>
            <ul>
              {pageSections.map((item) => (
                <li key={item.section}>
                  <strong>{item.section}:</strong> {item.question}
                </li>
              ))}
            </ul>
            <p>
              The playbook roadmap answers how it actually works with a four-step route, from picking a market to running the unit on systems. It ends at the moment that matters, "Your first guest checks in," with a line beneath it showing where the Scale program picks up.
            </p>
          </div>
          <div className="col-12 col-md-5">
            <CaseStudyImage image={images.roadmap} className="case-study-image--roadmap" />
          </div>
        </div>
        <div className="row pb-5 px-4">
          <div className="col-12">
            <p>
              Before any of that, the proof band shows that the playbook already works. It sticks to The Flex's published results: five million pounds in rent paid, 150+ corporate housing partners and 650+ tenants placed. Giving real examples of the success the founders have had with the playbook.
            </p>
            <CaseStudyImage image={images.proofBand} />
          </div>
        </div>
      </div>

      <div className="container">
        <div className="row py-5 px-4 bg-white--rounded">
          <div className="col-12">
            <h2>The Conversion Flows</h2>
            <p>
              The program comparison is the one place the page splits, placing both paths side by side at the moment of choosing. Everywhere else, a link to Scale sits within sight of every webinar button.
            </p>
            <CaseStudyImage image={images.comparison} className="case-study-image--centered" />
            <p className="pt-4">
              Both conversions open as modals, so visitors keep their place and the whole experience stays on a single page. Each flow asks only for what the next step needs.
            </p>
            <h3>The Webinar</h3>
            <p>
              Registration takes three fields: name, email and country. Choosing a country assigns the next session in the visitor's local time and shows it before they submit, so they know exactly what they're signing up for. The confirmation leads with the date and an add-to-calendar button.
            </p>
            <h3>The Strategy Call</h3>
            <p>
              Qualification is a short series of tap targets: how many units the visitor runs today, how many they're aiming for, their city and their budget. Budget comes last and is framed around fit, after a few easy answers have built some momentum. A calendar step follows, then a confirmation listing what to prepare for the call.
            </p>
            <CaseStudyImage image={images.registrationFlows} />
            <p className="mb-0 pt-3">
              Visitors who aren't ready for either step still have somewhere to go. A waitlist link sits beneath the Scale call ("Not ready to talk? Join the waitlist."), and a free short-term rental scaling checklist near the end of the page offers an easy way to stay engaged.
            </p>
          </div>
        </div>
      </div>

      <div className="container">
        <div className="row py-5 px-4 align-items-center">
          <div className="col-12 col-md-5">
            <h2>The Mobile Experience</h2>
            <p>
              For mobile, I focused on the hero and the program comparison, the section whose layout changes most from desktop. The hero keeps the webinar call to action above the fold with the Scale link directly beneath it. The comparison becomes two tabs, each showing its price, so the choice between paths still fits on one screen.
            </p>
          </div>
          <div className="col-12 col-md-7">
            <CaseStudyImage image={images.mobile} />
          </div>
        </div>
      </div>

      <div className="highlightSection">
        <svg className="highlightSection__house" viewBox="0 0 200 180" aria-hidden="true">
          <path d="M14 172 V78 L100 10 L186 78 V172" fill="none" stroke="currentColor" strokeWidth="8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <div className="container">
          <div className="row py-5 px-4">
            <div className="col-12 col-lg-8">
              <span className="eyebrow">If the concept were taken further</span>
              <h2>Next Steps</h2>
              <p>
                Since this was a technical assessment, here are the areas I'd explore next:
              </p>
            </div>
            <div className="col-12">
              <ol className="next-steps">
                {nextSteps.map((step, index) => (
                  <li key={step.title} className="next-step">
                    <span className="next-step__number">{String(index + 1).padStart(2, '0')}</span>
                    <h3 className="next-step__title">{step.title}</h3>
                    <p className="next-step__body">{step.description}</p>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      </div>

      <div className="container">
        <div className="row pt-5 px-4">
          <div className="col-12">
            <h2>Key Takeaways</h2>
          </div>
          {takeaways.map((takeaway) => (
            <div key={takeaway.title} className="col-12 col-md-6 d-flex">
              <div className="takeaway">
                <h3 className="takeaway__title">{takeaway.title}</h3>
                <p className="takeaway__body">{takeaway.body}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="row pb-5 px-4">
          <div className="col-12">
            <div className="explore-panel">
              <div className="explore-panel__text">
                <h3>Explore the design</h3>
                <p>
                  Click through both registration flows in the prototype, or open the Figma file to see every frame.
                </p>
              </div>
              <div className="explore-panel__actions">
                <a className="flex-button flex-button--primary" href={prototypeUrl} target="_blank" rel="noreferrer">View the Prototype</a>
                <a className="flex-button flex-button--outline" href={figmaFileUrl} target="_blank" rel="noreferrer">View the Figma File</a>
              </div>
            </div>
          </div>
        </div>
      </div>
      <CaseStudyBackLink />
    </section>
  );
}

export default FlexAcademy;
